import React, { useEffect, useRef, useState } from "react";

import { cn } from "utils";
import { useSnapshot } from "valtio";
import { ReportRatingProps } from "./types";
import styles from "./ReportRating.module.css";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/agent/AgentContent/AIXRay/Rating/ReportRating";
import { reportAccessStore } from "@/src/stores/reportAccessStore";
import { rootStore } from "@/src/store";
const I18n = I18nUtil.createScoped(Translations);

const NullReportRating = ({
  width = 75,
  height = 45,
  arcClassName,
  textClassName,
}: Pick<
  ReportRatingProps,
  "width" | "height" | "arcClassName" | "textClassName"
>) => {
  return (
    <div className="flex w-full h-full items-center gap-2 justify-center">
      <div
        className={styles.nullArea}
        style={{
          width: `${width}px`,
          height: `${height}px`,
        }}
      >
        {/* 空状态内容 */}
        <div className={styles.nullContent}>
          <div className={styles.arcContainer}>
            {/* 空状态评级弧度 - 只有灰色 */}
            <div className={`${styles.baseArc} ${arcClassName}`} />
          </div>
          {/* 空状态评级文字 */}
          <span
            className={cn(styles.nullText, "w-5 text-center", textClassName)}
          >
            --
          </span>
        </div>
      </div>
    </div>
  );
};

const NotNullReportRating = ({
  rating,
  id,
  symbol,
  width = 75,
  height = 45,
  type = "crypto",
  arcClassName,
  textClassName,
}: ReportRatingProps) => {
  const { isLoggedIn } = useSnapshot(rootStore.login);

  // 用于测量文本宽度的refs和state
  const spanRef = useRef<HTMLSpanElement>(null);
  const [dynamicWidth, setDynamicWidth] = useState<number>(75);

  // 获取评级对应的CSS类名
  const getRatingClassName = (rating: string) => {
    // 处理A+、A、A-、B+、B、B-等格式
    const normalizedRating =
      rating.replace(/[\+\-]/g, "").toUpperCase() +
      (rating.includes("+") ? "Plus" : rating.includes("-") ? "Minus" : "");
    const classMap = {
      APlus: styles.ratingAPlus,
      A: styles.ratingA,
      AMinus: styles.ratingAMinus,
      BPlus: styles.ratingBPlus,
      B: styles.ratingB,
      BMinus: styles.ratingBMinus,
      CPlus: styles.ratingCPlus,
      C: styles.ratingC,
      CMinus: styles.ratingCMinus,
      DPlus: styles.ratingDPlus,
      D: styles.ratingD,
      DMinus: styles.ratingDMinus,
    };
    return (
      classMap[normalizedRating as keyof typeof classMap] || styles.ratingDMinus
    );
  };

  const handleViewReport = (e: React.MouseEvent<HTMLDivElement>) => {
    // 总是阻止事件冒泡，避免触发表格行的点击事件
    e.stopPropagation();

    // 统一通过全局事件处理报告访问请求
    // 无需关心登录状态，全局管理器会自动处理
    reportAccessStore.requestReportAccess(
      { type, symbol, id },
      isLoggedIn ?? false,
    );
  };

  const ratingClassName = getRatingClassName(rating as any);

  // 提取翻译文本为变量以避免lint警告
  const fullReportText = I18n.t("Full Report");

  // 动态计算文本宽度
  useEffect(() => {
    if (spanRef.current) {
      // 创建一个临时元素来测量文本宽度
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (context) {
        // 获取span的计算样式
        const computedStyle = window.getComputedStyle(spanRef.current);
        context.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;

        // 测量文本宽度
        const textWidth = context.measureText(fullReportText).width;

        // 计算实际需要的宽度（包括padding）
        const paddingLeft = 4;
        const paddingRight = 4;
        const minWidth = 75;
        const calculatedWidth = Math.max(
          minWidth,
          Math.ceil(textWidth) + paddingLeft + paddingRight + 10,
        ); // 额外10px作为缓冲

        setDynamicWidth(calculatedWidth);
      }
    }
  }, [fullReportText]);

  return (
    <div
      data-openreplay-label="Rating · ReportRating · div"
      className="flex w-full h-full items-center gap-2 justify-center"
      onClick={handleViewReport}
    >
      <div
        className={styles.hoverArea}
        style={{
          width: `${width}px`,
          height: `${height}px`,
        }}
      >
        {/* 默认状态内容 */}
        <div className={styles.defaultContent}>
          <div className={styles.arcContainer}>
            {/* 评级弧度（包含彩色部分、间隙和灰色部分） */}
            <div
              className={`${styles.ratingArc} ${ratingClassName} ${arcClassName}`}
            />
          </div>
          {/* 评级文字 */}
          <span className={`${styles.text} ${textClassName}`}>{rating}</span>
        </div>

        {/* web端 Hover状态下的内容 */}
        <div
          className={cn(styles.hoverContent, "cursor-pointer")}
          style={{ minWidth: `${dynamicWidth}px` }}
        >
          <span ref={spanRef}>{fullReportText}</span>
        </div>
        {/* h5 */}
      </div>
      {/* <Image
        className={styles.h5Icon}
        src="/assets/images/ic_360_rating_h5.svg"
        alt="rating"
        width={20}
        height={20}
      /> */}
    </div>
  );
};

export const ReportRating = ({
  rating,
  id,
  symbol,
  width = 75,
  height = 45,
  type = "crypto",
  arcClassName,
  textClassName,
}: ReportRatingProps) => {
  if (!rating) {
    return (
      <NullReportRating
        width={width}
        height={height}
        arcClassName={arcClassName}
        textClassName={textClassName}
      />
    );
  }
  return (
    <NotNullReportRating
      arcClassName={arcClassName}
      rating={rating}
      id={id}
      symbol={symbol}
      width={width}
      height={height}
      type={type}
      textClassName={textClassName}
    />
  );
};
